import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { PageLayout, ActionButton, ImageUpload } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { getApiUrl } from "../../../config/env";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";

export const EditSlider: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation(["common"]);
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [formData, setFormData] = useState<{
    id: number;
    webImageUrl: string;
    mobileImageUrl: string;
    webImage: File | null;
    mobileImage: File | null;
    displayOrder: number;
    isActive: boolean;
  }>({
    id: 0,
    webImageUrl: "",
    mobileImageUrl: "",
    webImage: null,
    mobileImage: null,
    displayOrder: 0,
    isActive: true
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchSlider = async () => {
      try {
        const response = await adminApi.call(`/slider/${id}`, "GET");
        if (!response.success) {
          showErrorToast(response.error || "Failed to fetch slider");
          return;
        }
        setFormData({
          ...response.data,
          webImage: null,
          mobileImage: null
        });
      } catch (error) {
        showErrorToast("Error fetching slider");
        console.error("Error fetching slider:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSlider();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? Number(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.webImageUrl && !formData.webImage) {
      showErrorToast("Web image is required");
      return;
    }
    if (!formData.mobileImageUrl && !formData.mobileImage) {
      showErrorToast("Mobile image is required");
      return;
    }

    const data = new FormData();
    data.append('Id', formData.id.toString());
    
    if (formData.webImage) {
      data.append('WebImage', formData.webImage);
    } else {
      data.append('WebImageUrl', formData.webImageUrl);
    }

    if (formData.mobileImage) {
      data.append('MobileImage', formData.mobileImage);
    } else {
      data.append('MobileImageUrl', formData.mobileImageUrl);
    }

    data.append('DisplayOrder', formData.displayOrder.toString());
    data.append('IsActive', formData.isActive.toString());

    setUpdating(true);
    try {
      const response = await fetch(getApiUrl('/slider/admin/Update'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: data
      });

      if (!response.ok) {
        const error = await response.text();
        showErrorToast(error || "Failed to update slider");
        return;
      }

      showSuccessToast("Slider updated successfully");
      navigate("/cms/sliders");
    } catch (error) {
      showErrorToast("Error updating slider");
      console.error("Error updating slider:", error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Loading slider details...</div>;
  }

  return (
    <PageLayout
      title="Edit Slider"
      subtitle="Modify hero slider details"
      breadcrumbs={[{ label: "CMS" }, { label: "Sliders", path: "/cms/sliders" }, { label: "Edit" }]}
      className={isRTL ? "rtl" : "ltr"}
    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        <form onSubmit={handleSubmit}>
          <div className="row g-4">
            <div className="col-md-6">
              <ImageUpload
                label="Web Image (Wide)"
                value={formData.webImage || formData.webImageUrl}
                onChange={(file) => setFormData((prev) => ({ ...prev, webImage: file }))}
                placeholder="Upload web version"
              />
            </div>
            <div className="col-md-6">
              <ImageUpload
                label="Mobile Image (Tall/Square)"
                value={formData.mobileImage || formData.mobileImageUrl}
                onChange={(file) => setFormData((prev) => ({ ...prev, mobileImage: file }))}
                placeholder="Upload mobile version"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                className="form-control"
                name="displayOrder"
                value={formData.displayOrder}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-6 d-flex align-items-end">
              <div className="form-check form-switch mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="isActive">
                  Active (Show on website)
                </label>
              </div>
            </div>
          </div>

          <div className="text-center mt-5">
            <ActionButton
              variant="secondary"
              onClick={() => navigate("/cms/sliders")}
              className={isRTL ? "ms-2" : "me-2"}
              type="button"
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="primary"
              type="submit"
              loading={updating}
              disabled={updating}
            >
              Update Slider
            </ActionButton>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};
